import { apiClient } from "@/lib/api-client"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { AuthUserResponse } from "../entities/AuthUserResponse"

const showOtp =
  (import.meta.env.VITE_SHOW_OTP ?? "false").toLowerCase() === "true"

export interface SignupResponse {
  success: boolean
  message: string
  otp?: string
}

export const SignUpAsync = async (credentials: {
  userName: string
  password: string
  firstName: string
  lastName: string
  acceptedTermsVersion?: string
  acceptedPrivacyVersion?: string
  captchaToken?: string
}): Promise<SignupResponse> => {
  try {
    const res = await apiClient.post<SignupResponse>(`/auth/signup`, {
      email: credentials.userName,
      password: credentials.password,
      firstName: credentials.firstName,
      lastName: credentials.lastName,
      acceptedTermsVersion: credentials.acceptedTermsVersion,
      acceptedPrivacyVersion: credentials.acceptedPrivacyVersion,
      captchaToken: credentials.captchaToken ?? "",
    })

    if (!res.data.success) {
      throw new Error(res.data.message || "Signup failed");
    }

    if (showOtp && res.data.otp) {
      console.log(`[DEV MODE] OTP Code: ${res.data.otp}`)
      alert(
        `Development Mode: Your OTP code is ${res.data.otp}\n\n(This is shown because email sending failed. In production, you would receive this via email.)`
      )
    }

    return res.data
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error or server issue');
  }
};

export const LogoutAsync = async () => {
  try {
    await apiClient.post(`/auth/logout`)
    return true
  } catch (error) {
    console.warn("Logout API call failed, proceeding with local logout", error)
    return true
  }
}

export const FetchCurrentUserAsync = async () => {
  const res = await apiClient.get<AuthUserResponse>(`/auth/profile`)

  if (!res.status || res.status !== HttpStatusCodes.OK) {
    throw new Error("Failed to fetch profile")
  }
  return res.data as AuthUserResponse
}

export interface RefreshResponse {
  user: {
    id?: string;
    externalId?: string;
    status: number;
    email: string;
    firstName: string;
    lastName: string;
    createdAt?: string;
    lastLoginAt?: string;
    currentOrganizationId?: string | null;
    needsLegalConsent?: boolean;
    organizationRole?: string | null;
    organizationRoles?: string[];
  };
  roles: string[];
  permissions: string[];
  expiresAt: string;
}

export async function RefreshTokenAsync(): Promise<RefreshResponse> {
  const res = await apiClient.post<RefreshResponse>(`/auth/refresh`, {})

  if (!res.status || res.status !== HttpStatusCodes.OK) {
    throw new Error("Token refresh failed")
  }

  return res.data
}

export const ForgotPasswordAsync = async (
  email: string,
  captchaToken?: string,
): Promise<{ success: boolean; message: string }> => {
  const res = await apiClient.post<{ success: boolean; message: string }>(
    `/auth/forgot-password`,
    { email, captchaToken: captchaToken ?? "" }
  )

  if (!res.status || res.status !== HttpStatusCodes.OK) {
    throw new Error("Failed to send password reset email")
  }

  return res.data
}

export const SendChangePasswordLinkAsync = async (): Promise<{
  success: boolean
  message: string
}> => {
  const res = await apiClient.post<{ success: boolean; message: string }>(
    `/auth/send-change-password-link`,
    {},
  )
  return res.data
}

export const ChangePasswordAsync = async (payload: {
  email: string
  token: string
  oldPassword: string
  newPassword: string
  captchaToken?: string
}): Promise<{ success: boolean; message: string }> => {
  const res = await apiClient.post<{ success: boolean; message: string }>(
    `/auth/change-password`,
    { ...payload, captchaToken: payload.captchaToken ?? "" },
  )

  if (!res.status || res.status !== HttpStatusCodes.OK) {
    throw new Error("Failed to change password")
  }

  return res.data
}
export const ResetPasswordAsync = async (payload: {
  email: string
  token: string
  newPassword: string
  captchaToken?: string
}): Promise<{ success: boolean; message: string }> => {
  const res = await apiClient.post<{ success: boolean; message: string }>(
    `/auth/reset-password`,
    { ...payload, captchaToken: payload.captchaToken ?? "" }
  )

  if (!res.status || res.status !== HttpStatusCodes.OK) {
    throw new Error("Failed to reset password")
  }

  return res.data
}
export interface UserAccountInfo {
  firstName: string
  lastName: string
  phoneNumber: string
}

export const GetUserInfoByEmailAsync = async (email: string): Promise<UserAccountInfo | null> => {
  const res = await apiClient.get<UserAccountInfo | null>(`/auth/user-info`, {
    params: { email }
  })
  return res.data
}
